import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Trophy, Medal, Award, Clock, ChevronRight, RefreshCw, Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { projectAPI } from '../services/api';
import useUnifiedWebSocket from '../hooks/useUnifiedWebSocket';
import VictoryConfetti from './VictoryConfetti';

/**
 * Dramatic victory/completion screen for project endings
 * Shows different content for winner vs others in competitive mode
 * Auto-redirects after countdown
 */
const ProjectVictoryModal = ({ 
  isOpen, 
  project, 
  winner, 
  isYouWinner = false,
  currentUser,
  onClose,
  onRematchStarted = null // Callback for when rematch is started
}) => {
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState(15);
  const [rematchState, setRematchState] = useState({
    isStarted: false,
    startedBy: null,
    rematchProjectId: null,
    countdownSeconds: 0,
    isLoading: false,
    error: null
  });
  const navigate = useNavigate();
  const unified = useUnifiedWebSocket(null);

  // Fetch leaderboard data for competitive mode
  const { data: leaderboardData } = useQuery({
    queryKey: ['leaderboard', project?.id],
    queryFn: () => projectAPI.getLeaderboard(project.id),
    enabled: !!project?.id && isOpen && project?.gameMode === 'competitive',
    staleTime: 0,
  });

  // Fetch rematch status
  // Only fetch when project is actually finished or waiting (to avoid race conditions with ongoing->finished transition)
  const { data: rematchStatusData, error: rematchStatusError } = useQuery({
    queryKey: ['rematch-status', project?.id],
    queryFn: () => projectAPI.getRematchStatus(project.id),
    enabled: !!project?.id && isOpen && (project?.status === 'finished' || project?.status === 'waiting'),
    staleTime: 0,
    retry: false, // Don't retry on error to avoid spam
  });

  const standings = leaderboardData?.data?.data?.leaderboard || leaderboardData?.data?.leaderboard || [];

  const handleViewResults = useCallback(() => {
    onClose?.();
    // Navigate to finished project view
    if (project?.id) {
      // Pass justFinished state to prevent auto-redirect back to chat
      navigate(`/projects/${project.id}`, { state: { justFinished: true } });
    }
  }, [onClose, project?.id, navigate]);

  // Handle rematch creation
  const handleRematch = useCallback(async () => {
    if (!project?.id) return;
    
    setRematchState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await projectAPI.createRematch(project.id);
      const data = response?.data?.data;
      
      if (data?.rematchProjectId) {
        setRematchState(prev => ({
          ...prev,
          isStarted: true,
          startedBy: currentUser,
          rematchProjectId: data.rematchProjectId,
          countdownSeconds: data.countdownSeconds || 20,
          isLoading: false
        }));
        
        // Notify parent component
        onRematchStarted?.(data);
        
        // Navigate to waiting room
        navigate(`/projects/${data.rematchProjectId}`);
      }
    } catch (error) {
      console.error('Failed to create rematch:', error);
      setRematchState(prev => ({
        ...prev,
        isLoading: false,
        error: error?.response?.data?.detail || 'Failed to create rematch'
      }));
    }
  }, [project?.id, currentUser, onRematchStarted, navigate]);

  // Handle joining existing rematch
  const handleJoinRematch = useCallback(() => {
    if (rematchState.rematchProjectId) {
      navigate(`/projects/${rematchState.rematchProjectId}`);
    }
  }, [rematchState.rematchProjectId, navigate]);

  // Listen for rematch_started events via unified WebSocket on the project channel
  useEffect(() => {
    if (!isOpen || !project?.id) return;
    const channel = `project_${project.id}`;
    const unsubscribe = unified.subscribeToChannel(channel);
    const offRematch = unified.registerHandler(
      'rematch_started',
      (data) => {
        try {
          if (data?.originalProjectId === project.id) {
            setRematchState((prev) => ({
              ...prev,
              isStarted: true,
              startedBy: data.startedBy,
              rematchProjectId: data.rematchProjectId,
              countdownSeconds: data.countdownSeconds || 20
            }));
          }
        } catch (error) {
          console.error('Error handling rematch_started event:', error);
        }
      },
      channel
    );
    return () => {
      try { offRematch && offRematch(); } catch (_) {}
      try { unsubscribe && unsubscribe(); } catch (_) {}
    };
  }, [isOpen, project?.id, unified]);

  // Rematch countdown timer
  useEffect(() => {
    if (!rematchState.isStarted || rematchState.countdownSeconds <= 0) return;
    
    const timer = setInterval(() => {
      setRematchState(prev => ({
        ...prev,
        countdownSeconds: Math.max(0, prev.countdownSeconds - 1)
      }));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [rematchState.isStarted, rematchState.countdownSeconds]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Use setTimeout to avoid calling during render phase
          setTimeout(() => handleViewResults(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, countdown, handleViewResults]);

  // Mount/unmount animations
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      setCountdown(15); // Reset countdown
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
      const t = setTimeout(() => setIsMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Keyboard shortcuts (disabled - modal is non-closable)
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      // Only allow Enter to proceed to results
      if (e.key === 'Enter') handleViewResults();
      // Disable Escape key - modal is non-closable
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleViewResults]);


  if (!isMounted) return null;

  const isCompetitive = project?.gameMode === 'competitive';
  const isCollaborative = project?.gameMode === 'collaborative';
  // Note: We no longer use "pause" for Events - they finish when done
  // Sites use "End Session" (archive) instead of pause
  // This check is kept for backward compatibility with legacy endless projects
  const isEndless = project?.gameMode === 'endless' && project?.settings?.archetype !== 'event';
  const isSiteProject = project?.settings?.archetype === 'site';

  // Calculate project duration
  const projectDuration = project?.startedAt && project?.finishedAt
    ? Math.floor((new Date(project.finishedAt) - new Date(project.startedAt)) / 1000)
    : 0;
  const durationMinutes = Math.floor(projectDuration / 60);
  const durationSeconds = projectDuration % 60;

  // Get user's placement if competitive
  const userStanding = standings.find(s => s.userId === currentUser?.id);
  const userRank = userStanding?.rank || standings.findIndex(s => s.userId === currentUser?.id) + 1;

  // Get rank emoji
  const getRankEmoji = (rank) => {
    switch(rank) {
      case 1: return '👑';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `${rank}th`;
    }
  };

  const getDisplayName = (user) => {
    if (!user) return 'Someone';
    return user.firstName && user.lastName 
      ? `${user.firstName} ${user.lastName}` 
      : user.username || 'Someone';
  };

  const modalContent = (
    <>
      {/* Confetti */}
      <VictoryConfetti isWinner={isYouWinner} intensity={isYouWinner ? 60 : 30} />
      
      <div className="fixed inset-0 z-[10004]" role="dialog" aria-modal="true">
        {/* Backdrop (non-clickable) */}
        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-300 ${isVisible ? 'bg-opacity-70 opacity-100' : 'bg-opacity-0 opacity-0'}`} 
        />
        
        {/* Modal panel */}
        <div className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none">
          <div className={`relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 transform pointer-events-auto ${isVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}`}>
            
            {/* Close button removed - modal is non-closable */}

            {/* Content */}
            <div className="p-8 sm:p-12">
              {/* Trophy Icon */}
              <div className="flex justify-center mb-6">
                {isYouWinner ? (
                  <div className="relative animate-bounce">
                    <Trophy className="w-24 h-24 text-yellow-500" strokeWidth={1.5} />
                    <div className="absolute inset-0 bg-yellow-400 opacity-20 blur-xl animate-pulse" />
                  </div>
                ) : isCompetitive ? (
                  <Medal className="w-20 h-20 text-blue-500 animate-pulse" strokeWidth={1.5} />
                ) : (
                  <Award className="w-20 h-20 text-green-500 animate-pulse" strokeWidth={1.5} />
                )}
              </div>

              {/* Title */}
              <div className="text-center mb-8">
                {isCompetitive && isYouWinner ? (
                  <>
                    <h1 className="text-4xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 mb-2 animate-pulse">
                      YOU WON!
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-300">
                      {project?.name}
                    </p>
                  </>
                ) : isCompetitive ? (
                  <>
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                      Project Complete
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                      {project?.name}
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                      <Trophy className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <span className="text-blue-800 dark:text-blue-200 font-semibold">
                        {getDisplayName(winner)} won the project!
                      </span>
                    </div>
                  </>
                ) : isSiteProject ? (
                  <>
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                      Session Complete
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                      {project?.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Your session has been saved. Start a new session anytime from the project dashboard.
                    </p>
                  </>
                ) : (
                  <>
                    <h1 className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600 mb-2">
                      Project Complete
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-300">
                      {project?.name}
                    </p>
                  </>
                )}
              </div>

              {/* Stats & Standings */}
              {(isCompetitive || isCollaborative || projectDuration > 0) && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 mb-6">
                  {isCompetitive ? (
                  <>
                    {/* Competitive: Show standings */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-yellow-500" />
                      Final Standings
                    </h3>
                    <div className="space-y-3 mb-6">
                      {standings.slice(0, 5).map((standing, idx) => {
                        const isCurrentUser = standing.userId === currentUser?.id;
                        return (
                          <div 
                            key={standing.userId || idx}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              isCurrentUser 
                                ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500' 
                                : 'bg-white dark:bg-gray-800'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-2xl w-8 text-center">{getRankEmoji(standing.rank || idx + 1)}</span>
                              <span className={`font-semibold ${isCurrentUser ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'}`}>
                                {isCurrentUser ? 'You' : getDisplayName(standing.user)}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {standing.completedObjectives || 0}/{standing.totalObjectives || project?.objectives?.length || 0} objectives
                              </div>
                              {standing.completedAt && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(standing.completedAt).toLocaleTimeString()}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Your Performance */}
                    {!isYouWinner && userStanding && (
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Your Performance</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Placement:</span>
                            <span className="ml-2 font-semibold text-gray-900 dark:text-white">{getRankEmoji(userRank)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Completed:</span>
                            <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                              {userStanding.completedObjectives}/{userStanding.totalObjectives} objectives
                            </span>
                          </div>
                        </div>
                        {userStanding.totalCaptures > 0 && (
                          <div className="mt-2 text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Total captures:</span>
                            <span className="ml-2 font-semibold text-gray-900 dark:text-white">{userStanding.totalCaptures}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : isCollaborative ? (
                  <>
                    {/* Collaborative: Show team achievements */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Award className="w-5 h-5 text-green-500" />
                      Achievements
                    </h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {project?.objectives?.filter(o => o.isCompleted).length || 0}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Objectives Complete</div>
                      </div>
                      <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {/* participantCount = collaborators only, add 1 for owner */}
                          {project?.participantCount != null ? (1 + project.participantCount) : (standings.length || project?.participants?.length || 1)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Team Members
                        </div>
                      </div>
                    </div>

                    {/* Top Contributors */}
                    {standings.length > 0 && (
                      <>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 mt-4">Top Contributors</h4>
                        <div className="space-y-2">
                          {standings.slice(0, 3).map((standing, idx) => {
                            const isCurrentUser = standing.userId === currentUser?.id;
                            return (
                              <div 
                                key={standing.userId || idx}
                                className={`flex items-center justify-between p-2 rounded ${
                                  isCurrentUser ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-white dark:bg-gray-800'
                                }`}
                              >
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {idx + 1}. {isCurrentUser ? 'You' : getDisplayName(standing.user)}
                                </span>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                  {standing.totalCaptures || 0} captures
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                ) : null}

                {/* Project Duration */}
                {projectDuration > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>
                      Project duration: {durationMinutes > 0 && `${durationMinutes} min `}
                      {durationSeconds} sec
                    </span>
                  </div>
                )}
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3">
                {/* View Results */}
                <button
                  onClick={handleViewResults}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors relative overflow-hidden group"
                >
                  <span>View Results</span>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  
                  {/* Countdown indicator */}
                  <div className="absolute bottom-0 left-0 h-1 bg-blue-400 transition-all duration-1000" style={{ width: `${(countdown / 15) * 100}%` }} />
                </button>

                {/* Action Buttons - Contextual based on project type */}
                {isSiteProject ? (
                  // Site project: Session complete, can start new session
                  <button
                    onClick={() => {
                      onClose();
                      navigate(`/projects/${project.id}`);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
                  >
                    <span>Back to Dashboard</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                ) : isEndless ? (
                  // Legacy endless project: Show different content for owners vs participants
                  (isYouWinner || currentUser?.id === project?.ownerId) ? (
                    // Owner: Show continue info
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Continue Later</h3>
                      {/* <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                        Find this project in your "Endless Projects" tab to continue where you left off.
                      </p> */}
                      <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <span>Your progress is automatically saved</span>
                      </div>
                    </div>
                  ) : (
                    // Non-owner: Show project completed message
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                      <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">Project Completed</h3>
                      {/* <p className="text-sm text-green-700 dark:text-green-300 mb-3">
                        Great job! The project owner can continue this project later if they choose to.
                      </p> */}
                      <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>Your progress is automatically saved</span>
                      </div>
                    </div>
                  )
                ) : (
                  // Regular quests: Show rematch button
                  <>
                    {!rematchState.isStarted ? (
                      // Initial rematch button
                      <button
                        onClick={handleRematch}
                        disabled={rematchState.isLoading || !rematchStatusData?.data?.data?.canCreateRematch}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {rematchState.isLoading ? (
                          <>
                            <RefreshCw className="w-5 h-5 animate-spin" />
                            <span>Creating rematch...</span>
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-5 h-5" />
                            <span>
                              {isCompetitive ? 'Start Rematch' : 
                               isCollaborative ? 'Play Again' : 
                               'Explore Again'}
                            </span>
                          </>
                        )}
                      </button>
                    ) : (
                      // Rematch started by someone else
                      <button
                        onClick={handleJoinRematch}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
                      >
                        <Users className="w-5 h-5" />
                        <span>
                          {rematchState.startedBy?.id === currentUser?.id 
                            ? 'Rematch created! Waiting for others...'
                            : `${rematchState.startedBy?.firstName || rematchState.startedBy?.username || 'Someone'} started a rematch! Join in ${rematchState.countdownSeconds}s`
                          }
                        </span>
                      </button>
                    )}
                    
                    {/* Error message */}
                    {rematchState.error && (
                      <div className="mt-2 p-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-sm rounded">
                        {rematchState.error}
                      </div>
                    )}
                    
                    {/* Rematch limit reached */}
                    {!rematchStatusData?.data?.data?.canCreateRematch && !rematchState.isStarted && (
                      <div className="mt-2 p-2 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 text-sm rounded">
                        Maximum rematch attempts (3) reached for this project
                      </div>
                    )}
                    
                    {/* Rematch status query error */}
                    {rematchStatusError && (
                      <div className="mt-2 p-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-sm rounded">
                        Failed to check rematch status. Please refresh the page.
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Countdown text */}
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
                Auto-redirecting in {countdown}s...
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ProjectVictoryModal;

